import DownloadIcon from '@atlaskit/icon/glyph/download';
import Lozenge from '@atlaskit/lozenge';
import { N800 } from '@atlaskit/theme/colors';
import { InvoiceStatus, Refunds } from '@css-xp/graphtypes';
import { InvoicePaymentMethod } from '@css-xp/graphtypes/src/generated-types-new';
import { INVOICE_PATH_2, SourceSystem, INVOICE_PATH } from '@atlassian/cssxp-typescommon';
import { toNumber } from 'lodash';
import React from 'react';
import { Link } from 'react-router-dom';
import { PdfRedirectButton } from '../../../../account-subscriptions/billing-v2/invoices/src/invoice-page-utils';
import { PaymentErrorLozenge, RefundedLozenge } from '../../../copyable-text';
import './invoice-cell.scss';

interface InvoiceCellProps {
  invoiceNumber: string;
  status?: string | undefined | null;
  paymentError?: boolean | undefined;
  invoiceId?: string;
  transactionAccountId?: string;
  pdfLink: string | null | undefined;
  grandTotal: string | undefined | null | number;
  refunds?: Refunds[];
  invoiceLink?: string | undefined;
  sourceSystem?: SourceSystem | string | null;
  paymentMethod?: InvoicePaymentMethod | undefined | null;
}

export const isPaymentIssues = (status: string | undefined | null, paymentIssue = false) => {
  const PAYMENT_ERROR_STATUS: string[] = [
    InvoiceStatus.OPEN,
    InvoiceStatus.DRAFT,
    InvoiceStatus.PAYMENT_DEFERRED,
    InvoiceStatus.VOID,
  ];
  if (paymentIssue && status) {
    return PAYMENT_ERROR_STATUS.includes(status) ? <PaymentErrorLozenge /> : undefined;
  } else {
    return undefined;
  }
};

const isPaidInvoice = (status: string | undefined | null) => {
  if (!status) return;
  if (status === InvoiceStatus.PAID) {
    return true;
  } else {
    return false;
  }
};

const invoiceStatus = (status: string | undefined | null, paymentMethod: InvoicePaymentMethod | undefined | null) => {
  if (!status) return;
  if (paymentMethod && paymentMethod === InvoicePaymentMethod.DEFERRED && status === InvoiceStatus.PAYMENT_DEFERRED) {
    status = InvoiceStatus.PAID;
  }
  if (paymentMethod && paymentMethod === InvoicePaymentMethod.DEFERRED && status === InvoiceStatus.OPEN) {
    status = InvoiceStatus.PAYMENT_DEFERRED;
  }
  if (status === InvoiceStatus.PAID) {
    return (
      <span className="invoice-cell-lozenge">
        <Lozenge appearance="success" testId="success-status-lozenge">
          {status}
        </Lozenge>
      </span>
    );
  } else {
    return (
      <span className="invoice-cell-lozenge">
        <Lozenge appearance="inprogress" testId="inprogress-status-lozenge">
          {status}
        </Lozenge>
      </span>
    );
  }
};

const negativeInvoiceStatus = (grandTotal: string | undefined | null | number) => {
  if (grandTotal && toNumber(grandTotal) <= 0) {
    return (
      <span className="invoice-cell-lozenge">
        <Lozenge testId="negative-status-lozenge">NEGATIVE</Lozenge>
      </span>
    );
  }
  return null;
};

export const InvoiceCell = ({
  invoiceNumber,
  status,
  paymentError,
  pdfLink,
  transactionAccountId,
  invoiceId,
  grandTotal,
  refunds,
  invoiceLink,
  sourceSystem,
  paymentMethod,
}: InvoiceCellProps): JSX.Element => {
  const getInvoiceBasePath = () => {
    if (sourceSystem && sourceSystem.toLowerCase() === SourceSystem.HAMS.toLowerCase()) {
      return `${INVOICE_PATH}/${encodeURIComponent(invoiceSlug)}`;
    }
    return invoiceLink || `${INVOICE_PATH_2}/${encodeURIComponent(invoiceSlug)}`;
  };

  const invoiceSlug = invoiceNumber || (invoiceId ? invoiceId : '');
  return (
    <div className="invoice-slug-container" data-testid="invoice-slug-container" onClick={(e) => e.stopPropagation()}>
      <Link to={getInvoiceBasePath()} data-testid="invoice-link">
        <>{invoiceSlug}</>
      </Link>
      {isPaidInvoice(status) && (
        <PdfRedirectButton
          href={pdfLink ?? ''}
          label="View PDF"
          invoiceId={invoiceId}
          transactionAccountId={transactionAccountId}
          sourceSystem={sourceSystem as string}
        >
          <DownloadIcon
            aria-label="Download invoice PDF"
            label="Download invoice PDF"
            size="medium"
            testId="download-Icon"
            primaryColor={N800}
          />
        </PdfRedirectButton>
      )}
      <div>
        {invoiceStatus(status, paymentMethod)}
        {negativeInvoiceStatus(grandTotal)}
        {isPaymentIssues(status, paymentError)}
        {refunds && refunds.length > 0 && <RefundedLozenge />}
      </div>
    </div>
  );
};
